'use client'

import { yupResolver } from '@hookform/resolvers/yup'
import { LineFaceSmile } from '@qr-front/icons/line-face-smile'
import { SmartScrollContainer } from '@qr-front/ui/components/SmartScrollContainer'
import { GENERATOR_FORM_ANIMATION_DURATION } from 'features/generator/components/GeneratorFormSection'
import { useAtom } from 'jotai'
import { atomWithReset, RESET } from 'jotai/utils'
import { forwardRef, useImperativeHandle } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { validateContentForm } from 'utils/formUtils'

import { getStore } from '@/store'
import { TTranslationFunction } from '@/types'

import {
	TContentFormHandle,
	TQrContentFormSection,
} from '../qrContentFormTypes'
import { FACILITIES_LIST } from './facilitiesConfig'
import { getFacilitiesFormSchema } from './facilitiesSchema'
import { FacilityButton } from './FacilityButton'

export const FACILITIES_FORM_ID = 'facilities'

export const facilitiesAtom = atomWithReset<string[]>([])

export const resetFacilitiesForm = () => {
	getStore().set(facilitiesAtom, RESET)
}

export const FacilitiesForm = forwardRef<TContentFormHandle>((_, ref) => {
	const [facilities, setFacilities] = useAtom(facilitiesAtom)

	const form = useForm({
		defaultValues: { facilities },
		resolver: yupResolver(getFacilitiesFormSchema()),
		mode: 'onChange',
	})

	const { handleSubmit, setValue } = form

	useImperativeHandle(
		ref,
		() => ({
			validate: () =>
				validateContentForm(
					handleSubmit,
					GENERATOR_FORM_ANIMATION_DURATION * 1.25,
				),
		}),
		[handleSubmit],
	)

	const buttons = FACILITIES_LIST().map(({ id, name, icon: Icon }) => {
		const isSelected = facilities.includes(id)

		const toggle = () => {
			const updated = isSelected
				? facilities.filter((f) => f !== id)
				: [...facilities, id]
			setFacilities(updated)
			setValue('facilities', updated)
		}

		return (
			<li key={id}>
				<FacilityButton
					name={name}
					Icon={Icon}
					isSelected={isSelected}
					onClick={toggle}
				/>
			</li>
		)
	})

	return (
		<FormProvider {...form}>
			<form>
				<SmartScrollContainer
				// scrollbarPaddingHeight={16}
				>
					<ul className="flex gap-2 tablet:flex-wrap tablet:gap-4">
						{buttons}
					</ul>
				</SmartScrollContainer>
			</form>
		</FormProvider>
	)
})

FacilitiesForm.displayName = 'FacilitiesForm'

export const facilitiesFormConfig = (
	t: TTranslationFunction,
): TQrContentFormSection => ({
	id: FACILITIES_FORM_ID,
	title: t('generator__content_form_section__facilities__title'),
	description: t('generator__content_form_section__facilities__description'),
	icon: <LineFaceSmile />,
	Content: FacilitiesForm,
})
